'use client';

import type { FC } from 'react';
import { useTokens } from '../react/useTokens';
import { CompassMark } from './CompassMark';

export interface CompassWordmarkProps {
  readonly size?: number;
  readonly color?: string;
  readonly accent?: string;
}

/**
 * Compass mark + "BRAVEMOVE" wordmark, rendered dual-weight: BRAVE (Inter 900)
 * + MOVE (Inter 300), all-caps, tracking -0.03em. Reads typography and accent
 * color from the active theme via useTokens. Renders as plain JSX on web; on
 * mobile the SVG primitives swap to react-native-svg but the wordmark text
 * requires a host-specific renderer — this component is web-only by design.
 * For mobile, use CompassMark alone next to a host <Text>.
 */
export const CompassWordmark: FC<CompassWordmarkProps> = ({ size = 28, color, accent }) => {
  const t = useTokens();
  const inkColor = color ?? t.color.ink;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.34 }}>
      <CompassMark size={size * 1.6} color={inkColor} accent={accent ?? t.color.accent} />
      <span
        style={{
          fontFamily: t.type.display.family,
          fontSize: size,
          color: inkColor,
          letterSpacing: -size * 0.03,
          lineHeight: 1,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontWeight: 900 }}>BRAVE</span>
        <span style={{ fontWeight: 300 }}>MOVE</span>
      </span>
    </div>
  );
};
